import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setChecking(false);
      setPaymentConfirmed(true);
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentConfirmed(true);
        setChecking(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-12 text-center border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        {checking ? (
          <>
            <Loader2 className="w-16 h-16 text-[#8A9A86] mx-auto mb-6 animate-spin" />
            <h1 
              className="text-3xl font-light text-[#2C302E] mb-4"
              style={{fontFamily: 'Cormorant Garamond, serif'}}
            >
              {t('payment.checkingPayment')}
            </h1>
          </>
        ) : (
          <>
            <CheckCircle 
              data-testid="payment-success-icon" 
              className="w-20 h-20 text-[#8A9A86] mx-auto mb-6" 
            />
            <h1 
              className="text-4xl font-light text-[#2C302E] mb-4"
              style={{fontFamily: 'Cormorant Garamond, serif'}}
              data-testid="payment-success-title"
            >
              {t('payment.success')}
            </h1>
            <p 
              className="text-[#2C302E]/70 mb-8 leading-relaxed"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {t('payment.successMessage')}
            </p>
            {sessionId && (
              <div className="bg-[#F0F2EB] p-4 rounded-lg mb-8">
                <p className="text-sm text-[#2C302E]/60 mb-1">{t('payment.bookingCode')}</p>
                <p 
                  className="text-lg font-medium text-[#2C302E]"
                  data-testid="booking-code"
                >
                  {sessionId.substring(0, 8).toUpperCase()}
                </p>
              </div>
            )}
            <Button
              data-testid="back-to-rooms-button"
              onClick={() => navigate('/rooms')}
              className="w-full bg-[#8A9A86] hover:bg-[#7A8A76] text-white rounded-full py-6"
            >
              {t('payment.backToRooms')}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};
